import { observer } from 'mobx-react';
import { createElement, Fragment } from 'react';
import { ICON_BUTTON_ICONS } from '../../../../../components/buttons/IconButton';
import { formatNumber } from '../../../../../utils/utils';
import { PUNCTUATION } from '../../../../../utils/constants/notation';
import { getLogoUrl, LOGO_PLACEHOLDER } from '../../../../../utils/logo';
import pubSub from '../../../../../utils/pubsub';
import {
  highlightSearchKey,
  useContextMenuState,
  createKeyboardNavigationHandler
} from '../shared/profilerNameSearchUtils';
import ContextMenuTooltip from '../shared/ContextMenuTooltip';

/**
 * CompanyNameResultItem - Individual company result entry
 *
 * Displays a single company search result with:
 * - Company logo
 * - Company name (with search highlighting)
 * - Headquarters, revenue, employees, industries
 * - Click to navigate to company details (no data preload)
 * - Context menu for "open in new tab"
 * - Keyboard navigation (Enter, ArrowUp, ArrowDown)
 *
 * @param {Object} result - Company data object
 * @param {string} searchKey - Current search key for highlighting
 * @param {function} onNavigateToCompany - Callback to navigate to company details
 * @param {Object} utilsStore - Utils store for opening in new tab
 * @param {string} inputId - ID of the search input (for keyboard nav)
 * @param {string} entryId - DOM id of the entry, which the context menu anchors to. Must be unique
 *   on the page; override it where the same company can render twice (bulk review candidates).
 */
const CompanyNameResultItem = observer(
  ({
    result: company,
    searchKey,
    onNavigateToCompany,
    utilsStore,
    inputId = 'ps-input-searchKey',
    entryId = `profiler-name-search-result-entry-${company.id}`
  }) => {
    const state = useContextMenuState();

    // TD-1709: Navigate directly - no data preload needed
    const handleClick = () => {
      onNavigateToCompany(company);
    };

    // Open in new tab using URL-based navigation. Every context param the caller supplies (ref,
    // searchKey, searchId) goes into the URL, so the new tab loads the same way the page would.
    const handleOpenInNewTab = () => {
      onNavigateToCompany(company, null, ({ companyId, ...options }) => {
        utilsStore.openCompanyDetails(companyId, options);
      });
    };

    const handleKeyDown = createKeyboardNavigationHandler(handleClick, inputId);

    const logoUrl = getLogoUrl(company.website_SAFE_LOGO);

    return (
      <Fragment>
        <div
          id={entryId}
          className="profiler-name-search-result-entry"
          onContextMenu={(event) => {
            event.preventDefault();
            pubSub.publish({ event: 'icon-button-context-menu-trigger' });
            state.toggleLinkContextMenu();
          }}
          onClick={handleClick}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div className="info-wrap">
            {utilsStore.windowWidth >= 834 && (
              <div className="website-logo-wrap">
                <img
                  className="website-logo"
                  src={logoUrl || LOGO_PLACEHOLDER}
                />
              </div>
            )}
            <div className="inner-info-wrap">
              <div className="company-name-wrap">
                {utilsStore.windowWidth < 834 && (
                  <div className="website-logo-wrap-mobile">
                    <img
                      className="website-logo-mobile"
                      src={logoUrl || LOGO_PLACEHOLDER}
                    />
                  </div>
                )}
                <div className="company-name">
                  {highlightSearchKey(company.name, searchKey, createElement)}
                </div>
              </div>
              <div className="info-row">
                <div className="info-row-item" title="Company headquarters">
                  <div className="icon">
                    <img src={ICON_BUTTON_ICONS.GEO_ALT} />
                  </div>
                  <div className="content">{company.headquarters}</div>
                </div>
                <div className="item-separator" />
                <div
                  className="info-row-item"
                  title={`Company sales (in millions of euros)${
                    company.revenueYear ? ` in ${company.revenueYear}` : ''
                  }`}
                >
                  <div className="icon">
                    <img src={ICON_BUTTON_ICONS.CASH_STACK} />
                  </div>
                  <div className="content">
                    {!!company.revenue && (
                      <>
                        {company.revenue}
                        <span className="year">
                          &lsquo;
                          {company.revenueYear?.toString?.()?.slice?.(-2) ||
                            PUNCTUATION.EMPTY_VALUE}
                        </span>
                        &nbsp;€M
                      </>
                    )}
                    {!company.revenue && <>{PUNCTUATION.EMPTY_VALUE}</>}
                  </div>
                </div>
                <div className="item-separator" />
                <div
                  className="info-row-item"
                  title={`Company employees${
                    company.employeesYear ? ` in ${company.employeesYear}` : ''
                  }`}
                >
                  <div className="icon">
                    <img src={ICON_BUTTON_ICONS.PEOPLE} />
                  </div>
                  <div className="content">
                    {!isNaN(company.employees) && company.employees !== null ? (
                      <>
                        {formatNumber((+company.employees).toFixed(2))}
                        <span className="year">
                          &lsquo;
                          {company.employeesYear?.toString?.()?.slice?.(-2) ||
                            PUNCTUATION.EMPTY_VALUE}
                        </span>
                      </>
                    ) : (
                      PUNCTUATION.EMPTY_VALUE
                    )}
                  </div>
                </div>
                <div className="info-row-item industries">
                  {utilsStore.windowWidth < 834 ? (
                    <>
                      <div className="icon">
                        <img src={ICON_BUTTON_ICONS.BUILDING} />
                      </div>
                      <div className="content industries">
                        {!!company.industries.length && <>{company.industries.join(', ')}</>}
                        {!company.industries.length && <>{PUNCTUATION.EMPTY_VALUE}</>}
                      </div>
                    </>
                  ) : (
                    <div className="desktop-industries-wrap" title="Company industries">
                      <div className="icon">
                        <img src={ICON_BUTTON_ICONS.BUILDING} />
                      </div>
                      <div className="desktop-industries-text">
                        {!!company.industries.length && <>{company.industries.join(', ')}</>}
                        {!company.industries.length && <>{PUNCTUATION.EMPTY_VALUE}</>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {utilsStore.windowWidth < 834 && <div className="company-entry-separator" />}
        {state.toggledLinkContextMenu && (
          <ContextMenuTooltip anchorId={entryId} onOpenInNewTab={handleOpenInNewTab} />
        )}
      </Fragment>
    );
  }
);

export default CompanyNameResultItem;
